'use client'

// The connected wallet's Twetch notifications — replies, likes, reposts and
// follows — read straight from Twetch's public notification stream for the
// wallet's account. No Twetch session needed, so it works for any wallet
// tied to an account (a linked wallet too).
//
// "Seen" is kept in this browser: marking read on Twetch needs a session,
// which this site often does not have. Twetch's own read mark still counts —
// whichever is later wins, so reading on Twetch clears the badge here.

import React from 'react'
import useSwr from 'swr'
import { twetchJson, type TwetchPost, type TwetchUser } from './twetch-api'
import useViewerProfile from './use-viewer-profile'

export type TwetchNotificationType = 'reply' | 'like' | 'branch' | 'follow' | 'mention'

export interface TwetchNotification {
	id: number
	actorUserId: number
	type: string
	/// The viewer's post this is about.
	postId?: number
	/// The post the actor made (their reply or quote).
	actionPostId?: number
	description?: string
	createdAtMs: number
}

interface NotificationsPage {
	data: TwetchNotification[]
	users?: Record<string, TwetchUser>
	postContext?: { data?: TwetchPost[] }
	lastReadMs?: number
}

/// What this site shows. Twetch also streams "someone you follow posted"
/// rows, which are a feed, not a notification about you.
const SHOWN = new Set<string>(['reply', 'like', 'branch', 'follow', 'mention'])
const POLL_MS = 60_000
const PAGE = 40

const seenKey = (userId: number) => `alkanes-twetch-notifs-seen-${userId}`

function readSeen(userId: number): number {
	try {
		return Number(window.localStorage.getItem(seenKey(userId))) || 0
	} catch {
		return 0
	}
}

export default function useTwetchNotifications(): {
	/// Null until the wallet is known to have an account.
	userId: number | null
	items: TwetchNotification[]
	users: Record<string, TwetchUser>
	posts: Record<string, TwetchPost>
	unread: number
	loading: boolean
	isUnread: (item: TwetchNotification) => boolean
	markSeen: () => void
} {
	const { userId } = useViewerProfile()
	const { data, isLoading } = useSwr<NotificationsPage>(
		userId !== null ? `alkanes-twetch-notifications-${userId}` : null,
		() => twetchJson<NotificationsPage>(`/v1/users/${userId}/notifications?limit=${PAGE}`),
		{ refreshInterval: POLL_MS, revalidateOnFocus: true, shouldRetryOnError: false }
	)
	// This browser's "seen" mark, re-read whenever the account changes
	// (adjusted during render — an effect would paint one stale badge first).
	const [seenState, setSeenState] = React.useState<{ userId: number | null; at: number }>({
		userId: null,
		at: 0
	})
	if (seenState.userId !== userId) {
		setSeenState({ userId, at: userId !== null ? readSeen(userId) : 0 })
	}
	const localSeen = seenState.at

	const seen = Math.max(localSeen, data?.lastReadMs ?? 0)
	const items = React.useMemo(() => (data?.data ?? []).filter((n) => SHOWN.has(n.type)), [data])
	const posts = React.useMemo(() => {
		const out: Record<string, TwetchPost> = {}
		for (const post of data?.postContext?.data ?? []) {
			out[String(post.id)] = post
		}
		return out
	}, [data])
	const newest = items[0]?.createdAtMs ?? 0
	const markSeen = React.useCallback(() => {
		if (userId === null || newest <= localSeen) return
		setSeenState({ userId, at: newest })
		try {
			window.localStorage.setItem(seenKey(userId), String(newest))
		} catch {
			// Private mode: the badge clears for this visit only.
		}
	}, [userId, newest, localSeen])

	return {
		userId,
		items,
		users: data?.users ?? {},
		posts,
		unread: items.filter((n) => n.createdAtMs > seen).length,
		loading: isLoading,
		isUnread: (item) => item.createdAtMs > seen,
		markSeen
	}
}
